import * as React from "react";
import { Link } from "react-router-dom";
import { Card, Badge, List, Radio, Row, Button } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { customerTextBlockStore } from "../../CustomerInfo/CustomerTextBlockStore";
import { loginSettingsData } from "Pages/Login/Stores/LoginSettingsStore";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import paveLogo from "assets/img/pave-blue.png";
import dyveLogo from "assets/img/dyve-blue.jpg";
import pmgLogo from "assets/img/pmg-blue.jpg";
import docnodesLogo from "assets/img/docnodes.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { oXConfigStore } from "Pages/Admin/Stores/OXConfigStore";
import { releaseNotesStore } from "../../ReleaseNotes/ReleaseNotesStore";

interface UserInterfaceCardProps extends WithTranslation {}

interface UserInterfaceCardState {
  theme: API_ENVIRONMENTTHEMEDATA_THEME;
  loading: boolean;
}

export const UserInterfaceCard = withTranslation(I18N_NS.ADMIN)(
  observer(
    class extends React.Component<UserInterfaceCardProps, UserInterfaceCardState> {
      constructor(props: UserInterfaceCardProps) {
        super(props);
        this.state = {
          theme: loginSettingsData.data.theme,
          loading: false,
        };
      }

      componentWillUnmount() {
        // if user just clicked around without save - set theme from settings!
        if (loginSettingsData.data.theme !== this.state.theme) {
          loginSettingsData.setTheme(loginSettingsData.data.theme);
        }
      }

      handleChangeTheme = (e) => {
        this.setState({ theme: e.target.value });
        loginSettingsData.setTheme(e.target.value);
      };

      renderOXStatus = () => {
        const { t } = this.props;
        return status ? <Badge status="success" text={t("Active")} /> : <Badge status="error" text={t("Inactive")} />;
      };

      renderStatus = (status: boolean | string, env: AdminToolsEnv) => {
        const { t } = this.props;
        if (status !== null) {
          if (env === "INFO") {
            return status ? (
              <Badge status="success" text={t("Active")} />
            ) : (
              <Badge status="error" text={t("Inactive")} />
            );
          }
          if (env === "OX") {
            return status ? null : (
              <FontAwesomeIcon
                fixedWidth={true}
                size="lg"
                className={"text-warning"}
                icon={["far", "exclamation-circle"]}
              />
            );
          }
        }

        return null;
      };

      handleSave = async () => {
        try {
          this.setState({ loading: true });
          const formBody: API_EnvironmentThemeData = { theme: this.state.theme };
          await API.editData(API_URL.ADMIN.updateEnvironmentThemeData(), formBody);
          notificationSuccess(this.props.t("Success"), this.props.t("Layout was changed"));
          await loginSettingsData.fetch();
        } catch (error) {
          throw error;
        } finally {
          this.setState({ loading: false });
        }
      };

      render() {
        const { t } = this.props;
        const { theme, loading } = this.state;

        const linksData: AdminTools[] = [
          {
            label: "Release notes",
            link: "/admin/releaseNotes",
            status: releaseNotesStore.active,
            env: "INFO",
          },
          {
            label: "Customer information",
            link: "/admin/info",
            status: customerTextBlockStore.active,
            env: "INFO",
          },
          {
            label: "Whitelist of mail domains",
            link: "/admin/white-list",
            env: "DOMAINS",
          },
          {
            label: "Locked logins",
            link: "/admin/locked-logins",
            env: "LOGIN",
          },
          {
            label: "OX-interfaces",
            link: "/admin/ox-interfaces",
            status: oXConfigStore.oXSetupComplete,
            env: "OX",
          },
        ];

        return (
          <Card title={t("Environment settings")} className="mb-2">
            <List
              className="mb-md"
              bordered={false}
              dataSource={linksData}
              renderItem={(item: AdminTools) => {
                return (
                  <List.Item className="flex" style={{ justifyContent: "space-between" }}>
                    {item.link ? (
                      <Link to={item.link} className="link">
                        {t(item.label)}
                      </Link>
                    ) : (
                      t(item.label)
                    )}
                    {this.renderStatus(item.status, item.env)}
                  </List.Item>
                );
              }}
            />
            <h3>{t("User interface layout")}</h3>
            <Row justify="space-between">
              <Radio.Group onChange={this.handleChangeTheme} value={theme}>
                <Radio value={"PAVE"}>
                  <img src={paveLogo} alt="pave" style={{ display: "inline-block", width: 150 }} />
                </Radio>
                <Radio value={"DYVE"}>
                  <img src={dyveLogo} alt="dyve" style={{ display: "inline-block", width: 150 }} />
                </Radio>
                <Radio value={"DEFAULT"}>
                  <img src={pmgLogo} alt="dyve" style={{ display: "inline-block", width: 150 }} />
                </Radio>
                <Radio value={"DOCNODES"}>
                  <img src={docnodesLogo} alt="docnodes" style={{ display: "inline-block", width: 150 }} />
                </Radio>
              </Radio.Group>
              <Button type="primary" onClick={this.handleSave} loading={loading}>
                {t("Save")}
              </Button>
            </Row>
          </Card>
        );
      }
    }
  )
);
